import React, { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "profile";
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

const SEO: React.FC<SEOProps> = ({
  title = "Hardik Butani - Full stack Developer | Node.js, Nest.js, AWS Expert",
  description = "Experienced Full stack Developer specializing in Node.js, Nest.js, AWS, and Full-Stack Development. 5+ years of experience building scalable APIs, microservices, and cloud solutions.",
  keywords = "Full stack Developer, Node.js Developer, Nest.js Developer, AWS Developer, Full-Stack Developer, API Development, Microservices, Cloud Computing, Software Engineer, Database Design, REST APIs, GraphQL, MongoDB, PostgreSQL, Docker, Kubernetes, CI/CD, GitHub Actions, Team Leadership, Ahmedabad, India",
  image = "/assets/profile.webp",
  url = "https://www.hardikbutani.in/",
  type = "website",
  author = "Hardik Butani",
  publishedTime,
  modifiedTime,
  section,
  tags = [],
}) => {
  const fullUrl = url.startsWith("http")
    ? url
    : `https://www.hardikbutani.in${url}`;
  const fullImageUrl = image.startsWith("http")
    ? image
    : `https://www.hardikbutani.in${image}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyTag = (property: string, content: string) => {
      let meta = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update primary meta tags
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);
    updateMetaTag("author", author);

    // Update Open Graph tags
    updatePropertyTag("og:title", title);
    updatePropertyTag("og:description", description);
    updatePropertyTag("og:image", fullImageUrl);
    updatePropertyTag("og:url", fullUrl);
    updatePropertyTag("og:type", type);

    // Update Twitter tags
    updatePropertyTag("twitter:title", title);
    updatePropertyTag("twitter:description", description);
    updatePropertyTag("twitter:image", fullImageUrl);
    updatePropertyTag("twitter:url", fullUrl);

    // Update canonical URL
    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": type === "article" ? "Article" : "WebPage",
      name: title,
      description: description,
      url: fullUrl,
      image: fullImageUrl,
      author: {
        "@type": "Person",
        name: author,
        url: "https://www.hardikbutani.in/",
      },
      publisher: {
        "@type": "Person",
        name: author,
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": fullUrl,
      },
      ...(publishedTime && { datePublished: publishedTime }),
      ...(modifiedTime && { dateModified: modifiedTime }),
      ...(section && { articleSection: section }),
      ...(tags.length > 0 && { keywords: tags.join(", ") }),
    };

    // Remove existing structured data
    const existingScript = document.querySelector('script[data-seo="true"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "true");
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, [
    title,
    description,
    keywords,
    image,
    url,
    type,
    author,
    publishedTime,
    modifiedTime,
    section,
    tags,
    fullUrl,
    fullImageUrl,
  ]);

  return null; // This component doesn't render anything
};

export default SEO;
